import type { AppStoreLinks } from '@common/shared-ui-layer/types/app-store-links'

import type { BrandedAppMetadata } from '../types/branded-app'

const APP_STORE_HOST = 'apps.apple.com'
const PLAY_STORE_HOST = 'play.google.com'

/** Java package name — interpolated into an intent URI, so nothing else may pass. */
const ANDROID_PACKAGE_ID_RE = /^[A-Za-z]\w*(?:\.[A-Za-z]\w*)+$/

/**
 * Store links are tenant-controlled and get assigned to `window.location.href`,
 * so only https URLs on the expected store host are accepted.
 */
const toStoreUrl = (value: string | undefined, host: string): string | undefined => {
  if (!value) return undefined
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && url.hostname === host ? url.toString() : undefined
  } catch {
    // Malformed URL — treat the platform as having no link.
    return undefined
  }
}

/**
 * Resolve the banner's store links from the branded-app metadata. The app
 * `status` is not gated: an app can be live in the stores while its HighLevel
 * config is still a draft. Returns `null` when no platform has a valid link.
 */
export const resolveBrandedAppStoreLinks = (metadata: BrandedAppMetadata | null | undefined): AppStoreLinks | null => {
  const iosUrl = toStoreUrl(metadata?.distributionDetails?.appStoreAppLink, APP_STORE_HOST)
  const androidUrl = toStoreUrl(metadata?.distributionDetails?.playStoreAppLink, PLAY_STORE_HOST)
  if (!iosUrl && !androidUrl) return null

  const packageId = metadata?.appDetails?.googlePackageId
  const validPackageId = packageId && ANDROID_PACKAGE_ID_RE.test(packageId) ? packageId : undefined

  return {
    ...(iosUrl ? { ios: { storeUrl: iosUrl } } : {}),
    ...(androidUrl ? { android: validPackageId ? { storeUrl: androidUrl, packageId: validPackageId } : { storeUrl: androidUrl } } : {}),
  }
}
